import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useOverlayTriggerState } from 'react-stately';
import { useButton } from 'react-aria';

import { VIDEO_ERROR_I18N_KEYS, CategorizedVideoError } from '../video/webrtc/videoErrorUtils';

import { useVideo } from '../../../store/videos/useVideos';
import { useImage } from '../../../store/images/useImages';

import { useWebRTCConnection } from '../video/webrtc/useWebRTCConnection';
import { useI18n } from '../../../hooks/useI18nFormatters';

import { theme } from '../../../theme/theme';
import { su } from '../../../theme/functions/su';

import { LiveVideo } from '../video/LiveVideo';
import { CameraFullscreen, SourceRect } from '../video/CameraFullscreen';

import default_camera_image from '../images/camera/default_camera_image.png';
import { iconMuteCross } from '../../../theme/icons-v2/interface/mute-cross/mute-cross';
import { iconVolumeSet } from '../../../theme/icons-v2/interface/volume-set/volume-set';
import { iconFullscreen } from '../../../theme/icons-v2/interface/fullscreen/fullscreen';

import type { DeviceImage } from 'homey-api';

export interface LiveVideoViewProps {
  source?: {
    videoId?: string;
    deviceVideoId?: string;
    title?: string;
  };
  device?: {
    name?: string;
    images?: DeviceImage[];
  } | null;
  baseUrl?: string;
}

export function LiveVideoView(props: LiveVideoViewProps) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [includeAudio, setIncludeAudio] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [videoErrorMessage, setVideoErrorMessage] = useState('');
  const [sourceRect, setSourceRect] = useState<SourceRect | null>(null);

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const audioButtonRef = useRef<HTMLButtonElement>(null);
  const fullscreenButtonRef = useRef<HTMLButtonElement>(null);
  const fullscreenState = useOverlayTriggerState({});

  const { i18n } = useI18n();
  const { video } = useVideo({ videoId: props.source?.videoId ?? undefined });

  // Handle connection state changes from the hook
  function handleLoadingStateChange(loading: boolean) {
    setIsVideoLoading(loading);
    if (loading) {
      setHasVideoError(false);
      setVideoErrorMessage('');
    }
  }

  function handleVideoError(error: CategorizedVideoError | null) {
    if (error) {
      const i18nKey = VIDEO_ERROR_I18N_KEYS[error.category];
      const userMessage = i18n.messageFormatter(i18nKey);
      setHasVideoError(true);
      setVideoErrorMessage(userMessage);
      setIsVideoLoading(false);
      setIsPlaying(false);
    } else {
      setHasVideoError(false);
      setVideoErrorMessage('');
    }
  }

  // Manage WebRTC connection at this level - shared between inline and fullscreen views
  const { stream, isConnected, hasAudio, setAudioEnabled } = useWebRTCConnection({
    videoId: isPlaying ? video?.id : null, // Only connect when playing
    onLoadingStateChange: handleLoadingStateChange,
    onError: handleVideoError,
  });

  // Get the corresponding image for the snapshot background
  const correspondingImage = props.device?.images?.find(
    (img) => img.type === 'camera' && img.id === props.source?.deviceVideoId
  );
  const { image } = useImage({ imageId: correspondingImage?.imageObj?.id ?? undefined });

  // Reset playback when source changes (loading/error state is managed by hook callbacks)
  useEffect(() => {
    setIsPlaying(true);
  }, [props.source?.videoId]);

  function handleToggleAudio() {
    const newValue = !includeAudio;
    setIncludeAudio(newValue);
    setAudioEnabled(newValue);
  }

  function handleFullscreen() {
    const rect = videoContainerRef.current?.getBoundingClientRect();
    setSourceRect(rect ?? null);
    fullscreenState.open();
  }

  const audioButton = useButton({ onPress: handleToggleAudio }, audioButtonRef);
  const fullscreenButton = useButton(
    { onPress: handleFullscreen, isDisabled: hasVideoError },
    fullscreenButtonRef
  );

  const snapshotImageUrl =
    props.baseUrl && correspondingImage?.imageObj
      ? `${props.baseUrl}${correspondingImage.imageObj.url}${
          image?.lastUpdated ? `?${image.lastUpdated}` : ''
        }`
      : default_camera_image;

  const isLive = isPlaying && !isVideoLoading && !hasVideoError;

  return (
    <>
      <S.Container>
        <S.VideoContainer ref={videoContainerRef}>
          {video && (
            <LiveVideo
              key={video.id}
              playing={isPlaying}
              video={video}
              includeAudio={includeAudio}
              backgroundImage={snapshotImageUrl}
              stream={stream}
              isStreamConnected={isConnected}
              errorOverlay={
                hasVideoError ? (
                  <S.ErrorOverlayContent>
                    <S.ErrorIcon>!</S.ErrorIcon>
                    <S.ErrorMessage>{videoErrorMessage}</S.ErrorMessage>
                  </S.ErrorOverlayContent>
                ) : null
              }
            />
          )}

          {!video && <S.NoVideoMessage>No video available</S.NoVideoMessage>}
        </S.VideoContainer>

        <S.LiveStatus>
          {isLive && (
            <>
              <S.LiveDot />
              <S.LiveText>{i18n.messageFormatter('video.live')}</S.LiveText>
            </>
          )}
          {isPlaying && isVideoLoading && !hasVideoError && (
            <S.LiveText>{i18n.messageFormatter('video.loadingStream')}</S.LiveText>
          )}
        </S.LiveStatus>

        <S.ControlsContainer>
          {hasAudio && (
            <S.CircularButton
              {...audioButton.buttonProps}
              ref={audioButtonRef}
              data-is-pressed={audioButton.isPressed}
            >
              <S.ButtonIcon
                style={{
                  '--icon-url': `url(${includeAudio ? iconVolumeSet : iconMuteCross})`,
                }}
              />
            </S.CircularButton>
          )}

          <S.CircularButton
            {...fullscreenButton.buttonProps}
            ref={fullscreenButtonRef}
            data-is-pressed={fullscreenButton.isPressed}
          >
            <S.ButtonIcon
              style={{
                '--icon-url': `url(${iconFullscreen})`,
              }}
            />
          </S.CircularButton>
        </S.ControlsContainer>
      </S.Container>

      {video && (
        <CameraFullscreen
          isOpen={fullscreenState.isOpen}
          onClose={fullscreenState.close}
          sourceRect={sourceRect}
          video={video}
          backgroundImage={snapshotImageUrl}
          name={props.device?.name}
          cameraName={props.source?.title}
          isPlaying={isPlaying}
          includeAudio={includeAudio}
          onToggleAudio={handleToggleAudio}
          hasAudio={hasAudio}
          stream={stream}
          isStreamConnected={isConnected}
        />
      )}
    </>
  );
}

function S() {}
LiveVideoView.S = S;

S.Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

S.VideoContainer = styled.div`
  position: relative;
  width: 320px;
  aspect-ratio: 16 / 9;
  border-radius: ${theme.borderRadius.default};
  overflow: hidden;
  background-color: ${theme.color.black};
`;

S.LiveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(1)};
  margin-top: ${su(3)};
  height: 16px;
`;

S.LiveDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${theme.color.red};
`;

S.LiveText = styled.span`
  color: ${theme.color.white};
  font-size: 14px;
`;

S.ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(3)};
  margin-top: ${su(5)};
  margin-bottom: ${su(2)};
`;

S.CircularButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${theme.color.black};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &[data-is-pressed='true'] {
    transform: scale(0.95);
  }

  &[aria-disabled='true'] {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      opacity: 0.4;
    }

    &[data-is-pressed='true'] {
      transform: none;
    }
  }
`;

interface ButtonIconCSSProperties extends React.CSSProperties {
  '--icon-url': string;
}

S.ButtonIcon = styled.div<{ style: ButtonIconCSSProperties }>`
  width: 32px;
  height: 32px;
  background-color: ${theme.color.white};
  mask-image: var(--icon-url);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: var(--icon-url);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
`;

S.ErrorOverlayContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${su(1)};
`;

S.ErrorIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${theme.color.white};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.color.black};
  font-size: 14px;
  font-weight: bold;
`;

S.ErrorMessage = styled.span`
  color: ${theme.color.white};
  font-size: 14px;
  text-align: center;
  max-width: 200px;
`;

S.NoVideoMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${theme.color.text_inactive};
  font-size: 14px;
`;
